import type { PluginListenerHandle } from '@capacitor/core';

import { App } from '@capacitor/app';
import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { isAndroid } from 'src/utils/capacitor';

// ----------------------------------------------------------------------

/**
 * Global handler for the Android hardware/gesture back button.
 *
 * Capacitor's default back behaviour is unreliable with a react-router SPA -
 * client-side pushState navigations don't map cleanly onto the native WebView
 * history - so the OS back button appeared to do nothing and only the in-app
 * back buttons worked. Registering an explicit listener that drives react-router
 * (navigate(-1)) - the exact call the in-app back buttons use via useRouter().back
 * - makes the OS back button behave consistently, and exits the app when there's
 * no in-app history left to pop.
 *
 * Mounted once inside RootLayout so it has router context. Renders nothing.
 */
export function AndroidBackButton() {
  const navigate = useNavigate();
  // Keep the latest navigate without re-subscribing the native listener.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (!isAndroid()) return undefined;

    let handle: PluginListenerHandle | undefined;
    let cancelled = false;

    App.addListener('backButton', () => {
      // react-router stores its position in the history stack on history.state.idx.
      // idx > 0 means there's an in-app entry to pop; idx 0 (or absent) means we're
      // at the first entry, so exit the app instead of getting stuck.
      const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
      if (idx > 0) {
        navigateRef.current(-1);
      } else {
        App.exitApp();
      }
    }).then((listener) => {
      // Guard the async race: if the effect already cleaned up, remove immediately.
      if (cancelled) {
        listener.remove();
        return;
      }
      handle = listener;
    });

    return () => {
      cancelled = true;
      handle?.remove();
    };
  }, []);

  return null;
}
